import clsx from 'clsx'
import { useMemo } from 'react'

import { useGetTrackedProcessesListQuery } from '../../../app/apiSlice'
import { useGetTotalAppUsageTrendQuery } from '../../../app/apiSliceCoreLogicApi'
import { Loader } from '../../../common/components'
import { Text } from '../../../common/designs'
import { sortArrayOfObjectsBasedOnKeyStringValue } from '../../../common/utils/dataFormatting'
import { type ProcessAnalysisConfig, ProcessDataOriginEnum } from '../types'
import ProcessOriginBadge from './ProcessOriginBadge'
import ProcessSection from './ProcessSection'
import { getProcessOrCategoryMaxFourGridClass, yearAndWeekToKey } from './utils'

const UNDEFINED_CATEGORY = 'Other'

const ProcessList = () => {
  const { data, isFetching } = useGetTrackedProcessesListQuery()
  const { data: totalAppUsageTrend, isFetching: isFetchingtotalAppUsageTrend } =
    useGetTotalAppUsageTrendQuery()

  const { categorizedProcesses, unCategorizedProcesses } = useMemo(() => {
    const categorizedProcesses = {} as { [key: string]: ProcessAnalysisConfig[] }
    const unCategorizedProcesses = [] as ProcessAnalysisConfig[]
    data?.forEach((process: ProcessAnalysisConfig) => {
      if (!process.process_category) unCategorizedProcesses.push(process)
      else if (!(process.process_category in categorizedProcesses)) {
        categorizedProcesses[process.process_category] = [process]
      } else {
        categorizedProcesses[process.process_category].push(process)
      }
    })
    return {
      categorizedProcesses,
      unCategorizedProcesses,
    }
  }, [data])

  const totalAppUsageTrendMapper = useMemo(() => {
    if (!totalAppUsageTrend) return {}
    const mapper = {} as { [key: string]: number }
    totalAppUsageTrend.forEach(({ year, isoWeekNumber, total_duration_sec }) => {
      mapper[yearAndWeekToKey(year, isoWeekNumber)] = total_duration_sec
    })
    return mapper
  }, [totalAppUsageTrend])

  if (isFetching || isFetchingtotalAppUsageTrend) return <Loader />

  const hasUncategorizedProcesses = unCategorizedProcesses.length > 0
  const categorizedSectionCount = Object.keys(categorizedProcesses).length
  const totalProcessCount = data?.length ?? 0

  const categorizedProcessComponents = Object.entries(categorizedProcesses)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([category, processes]) => {
      return (
        <ProcessSection
          key={category}
          category={category}
          processes={sortArrayOfObjectsBasedOnKeyStringValue(processes, 'name')}
          totalAppUsageTrendMapper={totalAppUsageTrendMapper}
        />
      )
    })

  return (
    <div className='space-y-5 pb-3'>
      <section className='rounded-xl border border-slate-200/90 bg-gradient-to-r from-white via-white to-slate-50 p-5 shadow-sm'>
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-center lg:gap-x-0'>
          <header className='min-w-0 space-y-3 lg:pr-8'>
            <div>
              <Text size='2xl' weight={700}>
                Processes
              </Text>
              <Text size='s' color='gray' className='mt-1'>
                Explore tracked processes, grouped by category, and open a process for deeper
                analysis.
              </Text>
            </div>
            <div className='flex flex-wrap gap-2'>
              <div className='rounded-full border border-slate-200 bg-white px-2 py-0.5'>
                <span className='mr-1 text-[9px] font-medium uppercase tracking-wide text-slate-500'>
                  Total
                </span>
                <span className='text-xs font-semibold tabular-nums text-slate-800'>
                  {totalProcessCount}
                </span>
              </div>
              <div className='rounded-full border border-slate-200 bg-white px-2 py-0.5'>
                <span className='mr-1 text-[9px] font-medium uppercase tracking-wide text-slate-500'>
                  Categories
                </span>
                <span className='text-xs font-semibold tabular-nums text-slate-800'>
                  {categorizedSectionCount + (hasUncategorizedProcesses ? 1 : 0)}
                </span>
              </div>
            </div>
          </header>

          <div className='flex min-h-0 min-w-0 flex-col justify-center gap-3 border-0 lg:border-l lg:border-slate-200/90 lg:pl-8'>
            <Text size='xs' color='gray' className='leading-snug'>
              Source labels on each card match these badges:
            </Text>
            <div className='flex flex-col gap-3'>
              <span className='flex min-w-0 items-center gap-2.5'>
                <span className='inline-flex shrink-0'>
                  <ProcessOriginBadge origin={ProcessDataOriginEnum.TASK_MINER} />
                </span>
                <Text size='xs' color='gray' className='min-w-0 flex-1 leading-snug'>
                  Process data discovered via Process Intelligence Task Miners.
                </Text>
              </span>
              <span className='flex min-w-0 items-center gap-2.5'>
                <span className='inline-flex shrink-0'>
                  <ProcessOriginBadge origin={ProcessDataOriginEnum.PM_PLATFORM} />
                </span>
                <Text size='xs' color='gray' className='min-w-0 flex-1 leading-snug'>
                  Data synced from the ProcessMaker Platform integration.
                </Text>
              </span>
            </div>
          </div>
        </div>
      </section>

      {totalProcessCount === 0 ? (
        <section className='rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm'>
          <Text size='xl' weight={600}>
            No processes available
          </Text>
          <Text size='s' color='gray' className='mt-2'>
            Add or sync process definitions to start process discovery and analysis.
          </Text>
        </section>
      ) : categorizedSectionCount === 0 ? (
        <ProcessSection
          category='All processes'
          processes={sortArrayOfObjectsBasedOnKeyStringValue(unCategorizedProcesses, 'name')}
          totalAppUsageTrendMapper={totalAppUsageTrendMapper}
        />
      ) : (
        <div className={clsx('grid items-stretch', getProcessOrCategoryMaxFourGridClass())}>
          {categorizedProcessComponents}
          {hasUncategorizedProcesses ? (
            <ProcessSection
              key={UNDEFINED_CATEGORY}
              category={UNDEFINED_CATEGORY}
              processes={sortArrayOfObjectsBasedOnKeyStringValue(unCategorizedProcesses, 'name')}
              totalAppUsageTrendMapper={totalAppUsageTrendMapper}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

export default ProcessList

